// src/components/AchievementToast.js
// Fires a popup whenever the logged-in provider unlocks a new badge or levels up.
// State is persisted in localStorage so toasts only fire once per achievement.
import React, { useState, useEffect, useRef } from 'react';
import { getEarnedBadges, getLevel, LEVELS } from '../utils/gamification';
import './AchievementToast.css';

const STORAGE_PREFIX = 'wg_ach_';

const AchievementToast = ({ provider }) => {
  const [queue, setQueue]     = useState([]);
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(null);
  const timerRef = useRef(null);

  // Detect new unlocks whenever the provider profile changes
  useEffect(() => {
    if (!provider?.uid) return;

    const key    = `${STORAGE_PREFIX}${provider.uid}`;
    const stored = JSON.parse(localStorage.getItem(key) || '{}');
    const toasts = [];

    // ── Badge unlocks ───────────────────────────────────────────────────────
    const badges = getEarnedBadges(provider);
    badges.filter(b => b.earned).forEach(b => {
      if (!stored.badges?.includes(b.id)) {
        toasts.push({ emoji: b.emoji, title: 'Badge Unlocked!', desc: b.label });
      }
    });

    // ── Level-up ────────────────────────────────────────────────────────────
    const currentLevel = getLevel(provider.jobs || 0);
    if (stored.level && stored.level !== currentLevel.id) {
      const prevIdx = LEVELS.findIndex(l => l.id === stored.level);
      const curIdx  = LEVELS.findIndex(l => l.id === currentLevel.id);
      if (curIdx > prevIdx) {
        toasts.push({
          emoji: currentLevel.emoji,
          title: 'Level Up!',
          desc:  `You're now a ${currentLevel.label}!`,
        });
      }
    }

    // Persist the current snapshot so we don't re-fire
    const earnedIds = badges.filter(b => b.earned).map(b => b.id);
    localStorage.setItem(key, JSON.stringify({ badges: earnedIds, level: currentLevel.id }));

    if (toasts.length > 0) {
      setQueue(q => [...q, ...toasts]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    provider?.uid,
    provider?.jobs,
    provider?.credits,
    provider?.complaintsReceived,
    provider?.verified,
    provider?.leagueWins,
    provider?.fiveStarReviews,
    provider?.firstBids,
    provider?.totalBids,
    provider?.earlyAdopter,
  ]);

  const dismiss = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => setCurrent(null), 400); // wait for fade-out
  };

  // Dequeue — show one at a time, 4 s each
  useEffect(() => {
    if (current || queue.length === 0) return;
    setCurrent(queue[0]);
    setQueue(q => q.slice(1));
    setVisible(true);

    timerRef.current = setTimeout(dismiss, 4000);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, current]);

  if (!current) return null;

  return (
    <div className={`achievement-toast ${visible ? 'at-show' : 'at-hide'}`}>
      <button className="at-close" onClick={dismiss} aria-label="Dismiss">✕</button>
      <div className="at-glow" />
      <div className="at-emoji">{current.emoji}</div>
      <div className="at-body">
        <div className="at-title">{current.title}</div>
        <div className="at-desc">{current.desc}</div>
      </div>
    </div>
  );
};

export default AchievementToast;
